// Derives the dashboard header's upgrade call-to-action from billing state.
// Kept in its own module so it can be unit-tested without mounting Dashboard,
// which transitively pulls in `import.meta.env` and cannot run under Jest.

import type { BillingInfo } from '../../api/billing';

/** Credits are "low" once the balance drops under this share of the monthly grant. */
const LOW_CREDIT_RATIO = 0.2;

const creditFormatter = new Intl.NumberFormat('en-US');

export type UpgradeCtaUrgency = 'none' | 'low' | 'out';

export interface UpgradeCta {
  /** Whether to render the CTA at all. */
  visible: boolean;
  /** Balance line, or null when there is no trustworthy number to show yet. */
  creditsLabel: string | null;
  urgency: UpgradeCtaUrgency;
}

/**
 * @param billing Billing info for the current organization, or null if it is
 *   unavailable (no organization selected, or the request failed).
 * @param loading Whether the billing request is still in flight.
 */
export function getUpgradeCta(billing: BillingInfo | null, loading: boolean): UpgradeCta {
  // Enterprise has nothing to upgrade to. Everyone else — including users whose
  // billing we could not load — keeps the CTA, so it never silently vanishes.
  const visible = billing?.plan.type !== 'enterprise';

  const credits = billing?.plan.limits.credits;
  if (loading || !credits || credits.unlimited) {
    return { visible, creditsLabel: null, urgency: 'none' };
  }

  // The backend can report a negative balance when a job overruns its estimate.
  const remaining = Math.max(0, credits.remaining);

  let creditsLabel: string;
  if (remaining === 0) {
    creditsLabel = 'no credits left';
  } else if (remaining === 1) {
    creditsLabel = '1 credit left';
  } else {
    creditsLabel = `${creditFormatter.format(remaining)} credits left`;
  }

  let urgency: UpgradeCtaUrgency = 'none';
  if (remaining <= 0) {
    urgency = 'out';
  } else if (remaining < credits.limit * LOW_CREDIT_RATIO) {
    urgency = 'low';
  }

  return { visible, creditsLabel, urgency };
}
